import { z } from 'zod';

/**
 * Mise en route d'un cabinet neuf : assistant de première connexion, liste « Mise en route »,
 * visites guidées (`docs/plans/onboarding-et-aide.md`).
 *
 * L'état de chaque étape est **calculé par le serveur depuis les vraies données** — renseigner le
 * numéro d'autorisation fait passer l'étape à « fait » sans rien cocher. Seuls se stockent les
 * gestes de l'interface : assistant terminé ou passé, liste masquée, visites vues.
 */

export const onboardingStepKeys = [
  'cabinet_identity',
  'practitioner_authorization',
  'vat_regime',
  'dunning',
  'iban',
  'availability',
  'consultation_types',
  'first_patient',
  'first_appointment',
  'first_consultation',
  'first_invoice',
  'wero',
  'doctena',
  'two_factor',
  'reminders',
] as const;
export type OnboardingStepKey = (typeof onboardingStepKeys)[number];

/** Ce qu'il faut avant la première facture : identité du cabinet, autorisation, régime de TVA. */
export const onboardingRequiredSteps = [
  'cabinet_identity',
  'practitioner_authorization',
  'vat_regime',
] as const satisfies readonly OnboardingStepKey[];

export function isRequiredOnboardingStep(key: OnboardingStepKey): boolean {
  return (onboardingRequiredSteps as readonly OnboardingStepKey[]).includes(key);
}

/**
 * Nature affichée dans la liste. Une seule source, distincte de `required` :
 * celui-ci reste le critère « prêt à facturer » (identité, autorisation, TVA confirmée).
 * Obligatoire = sans quoi le cabinet ne facture ni n'exerce correctement.
 * Démarrage = premières actions guidées. Optionnel = intégrations, confort, franchise de TVA.
 */
export const onboardingStepKinds = ['required', 'starter', 'optional'] as const;
export type OnboardingStepKind = (typeof onboardingStepKinds)[number];

export const onboardingStepKindByKey = {
  cabinet_identity: 'required',
  practitioner_authorization: 'required',
  iban: 'required',
  availability: 'optional',
  consultation_types: 'required',
  first_patient: 'starter',
  first_appointment: 'starter',
  first_consultation: 'starter',
  first_invoice: 'starter',
  vat_regime: 'optional',
  dunning: 'optional',
  wero: 'optional',
  doctena: 'optional',
  two_factor: 'optional',
  reminders: 'optional',
} as const satisfies Record<OnboardingStepKey, OnboardingStepKind>;

/** Les quatre écrans qui ont une visite guidée. */
export const tourKeys = ['agenda', 'patient', 'consultation', 'invoice'] as const;
export type TourKey = (typeof tourKeys)[number];

export const onboardingWizardStates = ['pending', 'completed', 'skipped'] as const;
export type OnboardingWizardState = (typeof onboardingWizardStates)[number];

export interface OnboardingStepDto {
  key: OnboardingStepKey;
  done: boolean;
  required: boolean;
  /**
   * Faux quand l'étape relève d'un autre membre — un réglage du cabinet vu par un collaborateur :
   * elle s'affiche « faite par le titulaire », sans bouton.
   */
  actionable: boolean;
}

export interface OnboardingStatusDto {
  wizard: OnboardingWizardState;
  checklistHidden: boolean;
  /** Déjà filtrées par rôle et par offre : le front n'en retire rien. */
  steps: OnboardingStepDto[];
  toursSeen: TourKey[];
  /** Vrai quand toutes les étapes obligatoires de ce membre sont faites. */
  readyToInvoice: boolean;
  /** Courriels de démarrage du cabinet (bienvenue, relance J+5). */
  emailsEnabled: boolean;
  /** Seul le titulaire allume ou éteint ces courriels. */
  canManageEmails: boolean;
}

export const onboardingWizardSchema = z
  .object({ outcome: z.enum(['completed', 'skipped']) })
  .strict();
export type OnboardingWizardInput = z.infer<typeof onboardingWizardSchema>;

export const onboardingChecklistSchema = z.object({ hidden: z.boolean() }).strict();
export type OnboardingChecklistInput = z.infer<typeof onboardingChecklistSchema>;

export const onboardingTourSchema = z.object({ tour: z.enum(tourKeys) }).strict();
export type OnboardingTourInput = z.infer<typeof onboardingTourSchema>;

export const onboardingEmailsSchema = z.object({ enabled: z.boolean() }).strict();
export type OnboardingEmailsInput = z.infer<typeof onboardingEmailsSchema>;

/** Jalons d'un cabinet, lus par la console de plateforme : des dates, jamais un contenu. */
export const onboardingMilestoneKeys = [
  'ready_to_invoice',
  'first_patient',
  'first_appointment',
  'first_consultation',
  'first_invoice',
  'first_payment',
  'wero',
  'doctena',
  'second_member',
] as const;
export type OnboardingMilestoneKey = (typeof onboardingMilestoneKeys)[number];
